import { Modal, InlineLoading, Tag, UnorderedList, ListItem } from '@carbon/react';
import { useQuery } from '@tanstack/react-query';
import { ontologyApi } from '@/lib/api/ontology.api';

export interface OntologySourcesModalProps {
  isOpen: boolean;
  ontologyId?: string;
  onClose: () => void;
}

/**
 * OntologySourcesModal — lists the KB documents an ontology was generated from
 * (Feedback A17: "which sources led to this ontology?"). Empty when the ontology
 * was authored manually or from a prompt. Fetches on open.
 */
export const OntologySourcesModal = ({ isOpen, ontologyId, onClose }: OntologySourcesModalProps) => {
  const { data: sources, isLoading } = useQuery({
    queryKey: ['ontology-sources', ontologyId],
    queryFn: () => ontologyApi.getOntologySources(ontologyId as string),
    enabled: isOpen && !!ontologyId,
  });

  return (
    <Modal
      open={isOpen}
      modalHeading="Ontology sources"
      passiveModal
      onRequestClose={onClose}
      size="sm"
    >
      {isLoading && <InlineLoading description="Loading sources…" />}

      {!isLoading && (!sources || sources.length === 0) && (
        <p data-testid="ontology-sources-empty">
          No knowledge base sources are recorded for this ontology. It was created manually
          or from a prompt, not generated from selected documents.
        </p>
      )}

      {!isLoading && sources && sources.length > 0 && (
        <>
          <p style={{ marginBottom: '0.75rem' }}>
            Generated from {sources.length} document{sources.length !== 1 ? 's' : ''}:
          </p>
          <UnorderedList data-testid="ontology-sources-list">
            {sources.map((s) => (
              <ListItem key={s.knowledge_object_id}>
                {s.display_name || s.filename}
                {s.knowledge_base_name && (
                  <Tag size="sm" type="outline" style={{ marginLeft: '0.5rem' }}>
                    {s.knowledge_base_name}
                  </Tag>
                )}
              </ListItem>
            ))}
          </UnorderedList>
        </>
      )}
    </Modal>
  );
};
